import {
  DestroyRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Directive({
  selector: '[scrollbarOverContent]',
  standalone: true,
})
export class ScrollbarOverContentDirective implements OnInit, OnDestroy {
  public readonly elementRef = inject(ElementRef);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private destroyRef = inject(DestroyRef);
  private scrollbar: HTMLDivElement | null = null;
  private isDragging = false;
  private isMouseOut = false;

  public ngOnInit(): void {
    this.scrollbar = document.createElement('div');
    this.setStyles();
    this.scrollbar.addEventListener('mousedown', this.mouseDown);
    this.elementRef.nativeElement.appendChild(this.scrollbar);
  }

  public ngOnDestroy(): void {
    this.scrollbar.removeEventListener('mousedown', this.mouseDown);
    this.scrollbar.remove();
    this.scrollbar = null;
  }

  public mouseDown = (event: MouseEvent): void => {
    event.preventDefault();
    this.isDragging = true;

    const scrollTop = this.elementRef.nativeElement.scrollTop;
    const height = this.elementRef.nativeElement.scrollHeight;
    const clientHeight = this.elementRef.nativeElement.clientHeight;
    const scrollHeight = height - clientHeight;

    const start = event.clientY;

    const mouseMove = fromEvent(document, 'mousemove').pipe(takeUntilDestroyed(this.destroyRef));
    const mouseUp = fromEvent(document, 'mouseup').pipe(takeUntilDestroyed(this.destroyRef));

    const subMouseMove = mouseMove.subscribe((e: any) => {
      const y = e.clientY - start;
      const scrollPx = y * scrollHeight / clientHeight;
      this.elementRef.nativeElement.scrollTop = scrollTop + scrollPx;
      requestAnimationFrame(() => this.updateScrollbarTop());
    });

    const subMouseUp = mouseUp.subscribe(() => {
      subMouseMove.unsubscribe();
      subMouseUp.unsubscribe();
      this.isDragging = false;
      if (this.isMouseOut) this.mouseLeave();
    });
  }

  @HostListener('mouseenter')
  public mouseEnter(): void {
    this.isMouseOut = false;
    this.renderer.setStyle(this.scrollbar, 'opacity', '1');
  }

  @HostListener('mouseleave')
  public mouseLeave(): void {
    this.isMouseOut = true;
    if (!this.isDragging) this.renderer.setStyle(this.scrollbar, 'opacity', '0');
  }

  @HostListener('scroll')
  public scroll(): void {
    if (this.isDragging) return;
    this.updateScrollbarTop();
  }

  private updateScrollbarTop(): void {
    const scrollTop = this.elementRef.nativeElement.scrollTop;
    const height = this.elementRef.nativeElement.scrollHeight;
    const clientHeight = this.elementRef.nativeElement.clientHeight;
    const scrollHeight = height - clientHeight;
    const scrollPx = (scrollTop + (scrollTop / scrollHeight) * (clientHeight - this.scrollbar.clientHeight));
    this.renderer.setStyle(this.scrollbar, 'top', `${scrollPx}px`);
  }


  private setStyles(): void {
    this.renderer.addClass(this.elementRef.nativeElement, 'scrollbar-none');
    this.renderer.addClass(this.elementRef.nativeElement, 'relative');
    this.renderer.addClass(this.elementRef.nativeElement, 'overflow-auto');

    requestAnimationFrame(() => {
      const proportionHeight = (this.elementRef.nativeElement.clientHeight / this.elementRef.nativeElement.scrollHeight) * this.elementRef.nativeElement.clientHeight;

      this.renderer.setStyle(this.scrollbar, 'padding', '3px');
      this.renderer.setStyle(this.scrollbar, 'width', '15px');
      this.renderer.setStyle(this.scrollbar, 'height', `${Math.max(proportionHeight, 56)}px`);
      this.renderer.setStyle(this.scrollbar, 'position', 'absolute');
      this.renderer.setStyle(this.scrollbar, 'right', '0');
      this.renderer.setStyle(this.scrollbar, 'top', '0');
      this.renderer.setStyle(this.scrollbar, 'opacity', '0');
      this.renderer.setStyle(this.scrollbar, 'cursor', 'pointer');
      this.renderer.setStyle(this.scrollbar, 'zIndex', '100');

      const scrollbarThumb = document.createElement('div');
      this.renderer.addClass(scrollbarThumb, 'bg-scroll-opacity');
      this.renderer.addClass(scrollbarThumb, 'hover:bg-scroll');
      this.renderer.addClass(scrollbarThumb, 'w-full');
      this.renderer.addClass(scrollbarThumb, 'h-full');
      this.renderer.addClass(scrollbarThumb, 'border-radius-8');
      this.renderer.addClass(scrollbarThumb, 'cursor-pointer');

      this.scrollbar.appendChild(scrollbarThumb);
    })

  }
}
